import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../../../services/api';
import { today, yearStart } from '../utils/helpers';
import { SEC_DEFAULT } from '../utils/constants';

export const useDecisionnel = () => {
  /* ── filtres affichés (input) vs appliqués ────────────────────────────────── */
  const [debut,      setDebut]      = useState(yearStart());
  const [fin,        setFin]        = useState(today());
  const [inputDebut, setInputDebut] = useState(yearStart());
  const [inputFin,   setInputFin]   = useState(today());

  const [villeOpt,           setVilleOpt]           = useState(null);
  const [projetOpts,         setProjetOpts]          = useState([]);
  const [trancheOpt,         setTrancheOpt]          = useState(null);
  const [typeOpt,            setTypeOpt]             = useState(null);
  const [comOpt,             setComOpt]              = useState(null);
  const [titreOpt,           setTitreOpt]            = useState([]);
  const [statutNotaireOpts,  setStatutNotaireOpts]   = useState(['CHEZ', 'EN_COURS']);
  const [notaireOpts,        setNotaireOpts]         = useState([]);
  const [notairesList,       setNotairesList]        = useState([]);

  /* ── données ──────────────────────────────────────────────────────────────── */
  const [filters,      setFilters]      = useState(null);
  const [kpi,          setKpi]          = useState(null);
  const [graphique,    setGraphique]    = useState(null);
  const [tableau,      setTableau]      = useState([]);
  const [detailVentes, setDetailVentes] = useState([]);
  const [detailRec,    setDetailRec]    = useState([]);
  const [ptfAvant,     setPtfAvant]     = useState(0);
  const [ptfApres,     setPtfApres]     = useState(0);
  const [ptfAvantRows, setPtfAvantRows] = useState([]);
  const [ptfApresRows, setPtfApresRows] = useState([]);
  const [detailPn,     setDetailPn]     = useState([]);
  const [projetKpi,    setProjetKpi]    = useState([]);
  const [commercialKpi,setCommercialKpi]= useState([]);
  const [budgetParType,setBudgetParType]= useState([]);
  const [budgetParTypeCA,setBudgetParTypeCA]= useState({});
  const [stockDestockage,setStockDestockage]= useState([]);
  const [includeOldSigned, setIncludeOldSigned] = useState(false);
  const [oldSignedBefore,  setOldSignedBefore]  = useState('2024-01-01');
  const [stockTypeOpts,    setStockTypeOpts]    = useState([]);
  const [showStockFilters, setShowStockFilters] = useState(true);
  const [detailBudget, setDetailBudget] = useState([]);
  const [detailStockCa, setDetailStockCa] = useState([]);
  const [detailStockCumul, setDetailStockCumul] = useState([]);
  const [detailStockInitial, setDetailStockInitial] = useState([]);
  const [detailNS,     setDetailNS]     = useState([]);
  const [nsRows,       setNsRows]       = useState([]);
  const [lotsRows,              setLotsRows]              = useState([]);
  const [encoursRows,           setEncoursRows]           = useState([]);
  const [situationCommerciale,  setSituationCommerciale]  = useState([]);
  const [dureeData,       setDureeData]       = useState(null);
  const [lastFetch,    setLastFetch]    = useState(null);

  /* ── loading ──────────────────────────────────────────────────────────────── */
  const [loadF,    setLoadF]    = useState(true);
  const [loadK,    setLoadK]    = useState(false);
  const [loadG,    setLoadG]    = useState(false);
  const [loadT,    setLoadT]    = useState(false);
  const [loadDV,   setLoadDV]   = useState(false);
  const [loadDR,   setLoadDR]   = useState(false);
  const [loadDP,   setLoadDP]   = useState(false);
  const [loadPK,   setLoadPK]   = useState(false);
  const [loadCK,   setLoadCK]   = useState(false);
  const [loadBPT,  setLoadBPT]  = useState(false);
  const [loadSD,   setLoadSD]   = useState(false);
  const [loadDB,   setLoadDB]   = useState(false);
  const [loadDSC,  setLoadDSC]  = useState(false);
  const [loadDSCu, setLoadDSCu] = useState(false);
  const [loadDSI,  setLoadDSI]  = useState(false);
  const [loadDNS,  setLoadDNS]  = useState(false);
  const [loadNS,   setLoadNS]   = useState(false);
  const [loadLots,              setLoadLots]              = useState(false);
  const [loadEncours,           setLoadEncours]           = useState(false);
  const [loadSitComm,           setLoadSitComm]           = useState(false);
  const [loadDuree,       setLoadDuree]       = useState(false);
  const [error,    setError]    = useState('');

  /* ── UI state ─────────────────────────────────────────────────────────────── */
  const [activeDetail,    setActiveDetail]    = useState(null);
  const [showAllProjets,  setShowAllProjets]  = useState(false);
  const [showAllCom,      setShowAllCom]      = useState(false);
  const [comConfigOpen,   setComConfigOpen]   = useState(false);
  const [comHidden,       setComHidden]       = useState(() => {
    try { const raw = localStorage.getItem('decisionnel_com_hidden'); return raw ? new Set(JSON.parse(raw)) : new Set(); } catch { return new Set(); }
  });
  const [sortKey,  setSortKey]  = useState('caTotal');
  const [sortDir,  setSortDir]  = useState(-1);
  const [sectionOrder, setSectionOrder] = useState(() => {
    try { const s = localStorage.getItem('dec_sec_order'); if (s) { const p = JSON.parse(s); return [...p.filter(id => SEC_DEFAULT.includes(id)), ...SEC_DEFAULT.filter(id => !p.includes(id))]; } } catch {}
    return [...SEC_DEFAULT];
  });
  const [sectionCollapsed, setSectionCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dec_sec_collapsed') || '{}'); } catch { return {}; }
  });
  // Unité monétaire choisie par section (Auto/MAD/kMAD/MMAD), persistée en localStorage
  const [sectionUnits, setSectionUnits] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dec_sec_units') || '{}'); } catch { return {}; }
  });
  const setUnit = (id, u) => setSectionUnits(prev => {
    const next = { ...prev, [id]: u };
    try { localStorage.setItem('dec_sec_units', JSON.stringify(next)); } catch {}
    return next;
  });
  const [dragSecId,     setDragSecId]     = useState(null);
  const [dragOverSecId, setDragOverSecId] = useState(null);

  /* ── refs ─────────────────────────────────────────────────────────────────── */
  const filtersRef       = useRef(null);
  const debounceRef      = useRef(null);
  const prevVille        = useRef(null);
  const cacheSkipLoadRef = useRef(false); // true si le cache est frais → skip le loadData initial

  /* ── memos ────────────────────────────────────────────────────────────────── */
  const projetsDisponibles = useMemo(() => {
    if (!filters?.projets) return [];
    if (!villeOpt) return filters.projets;
    return filters.projets.filter(p => p.cityId === villeOpt.value);
  }, [filters, villeOpt]);

  const filterProjetIds = useMemo(() => {
    if (projetOpts.length)                       return projetOpts.map(o => o.value);
    if (villeOpt && projetsDisponibles.length)   return projetsDisponibles.map(p => p.id);
    return [];
  }, [projetOpts, villeOpt, projetsDisponibles]);

  const activeFilterPills = useMemo(() => {
    const pills = [];
    const fmtD = d => d ? d.split('-').reverse().join('/') : '';
    pills.push({ key: 'period', label: `${fmtD(debut)} → ${fmtD(fin)}` });
    if (villeOpt)           pills.push({ key: 'ville',   label: `Ville: ${villeOpt.label}` });
    if (projetOpts?.length) pills.push({ key: 'projet',  label: projetOpts.length === 1 ? `Projet: ${projetOpts[0].label}` : `${projetOpts.length} projets` });
    if (trancheOpt)         pills.push({ key: 'tranche', label: `Tranche: ${trancheOpt.label}` });
    if (typeOpt)            pills.push({ key: 'type',    label: `Type: ${typeOpt.label}` });
    if (comOpt)             pills.push({ key: 'com',     label: `Commercial: ${comOpt.label}` });
    if (titreOpt?.length)   pills.push({ key: 'titre',   label: titreOpt.length === 1 ? `Titre: ${titreOpt[0].label}` : `Titre: ${titreOpt.map(o => o.label).join(', ')}` });
    return pills;
  }, [debut, fin, villeOpt, projetOpts, trancheOpt, typeOpt, comOpt, titreOpt]);

  const sorted = useMemo(() =>
    [...tableau].sort((a, b) => {
      const va = a[sortKey], vb = b[sortKey];
      if (typeof va === 'string') return sortDir * va.localeCompare(vb);
      return sortDir * ((va || 0) - (vb || 0));
    }), [tableau, sortKey, sortDir]);

  const datesDirty = inputDebut !== debut || inputFin !== fin;
  const hasFilters = villeOpt || projetOpts.length || trancheOpt || typeOpt || comOpt || titreOpt.length || debut !== yearStart() || fin !== today();

  /* ── buildParams ──────────────────────────────────────────────────────────── */
  const buildParams = useCallback((overrideDebut, overrideFin) => {
    const params = { debut: overrideDebut ?? debut, fin: overrideFin ?? fin };
    if (projetOpts.length)               params.projetIds  = projetOpts.map(o => o.value).join(',');
    else if (villeOpt && projetsDisponibles.length) params.projetIds = projetsDisponibles.map(p => p.id).join(',');
    if (trancheOpt)         params.tranche    = trancheOpt.value;
    if (typeOpt)            params.type       = typeOpt.value;
    if (comOpt)             params.comId      = comOpt.value;
    if (titreOpt.length)    params.titre      = titreOpt.map(o => o.value).join(',');
    if (notaireOpts.length) params.notaireIds = notaireOpts.join(',');
    return params;
  }, [debut, fin, projetOpts, villeOpt, projetsDisponibles, trancheOpt, typeOpt, comOpt, titreOpt, notaireOpts]);

  /* ── loadData ─────────────────────────────────────────────────────────────── */
  const loadData = useCallback((overrideDebut, overrideFin) => {
    const params = buildParams(overrideDebut, overrideFin);
    const sdParams = { ...params };
    if (includeOldSigned) { sdParams.includeOldSigned = '1'; sdParams.oldSignedBefore = oldSignedBefore; }
    if (stockTypeOpts.length) sdParams.stockTypes = stockTypeOpts.join(',');
    setLoadK(true); setLoadG(true); setLoadT(true);
    setLoadDV(true); setLoadDR(true); setLoadDP(true); setLoadBPT(true); setLoadSD(true); setLoadPK(true); setLoadCK(true); setLoadDB(true); setLoadDSC(true); setLoadDuree(true);
    setLoadNS(true);
    setError('');

    /* ── Phase 1 : données critiques (visibles immédiatement) ── */
    Promise.all([
      api.get('/decisionnel/kpi',              { params }),
      api.get('/decisionnel/graphique',        { params }),
      api.get('/decisionnel/tableau',          { params }),
      api.get('/decisionnel/budget-par-type',  { params }),
      api.get('/decisionnel/stock-destockage', { params: sdParams }),
      api.get('/decisionnel/projet-kpi',       { params }),
      api.get('/decisionnel/commercial-kpi',   { params }),
    ])
      .then(([kpiR, graphR, tabR, bptR, sdR, pkR, ckR]) => {
        setKpi(kpiR.data); setGraphique(graphR.data); setTableau(tabR.data);
        setBudgetParType(bptR.data.rows || []); setBudgetParTypeCA(bptR.data.budgets || {});
        setStockDestockage(sdR.data || []); setProjetKpi(pkR.data); setCommercialKpi(ckR.data);
        setLoadK(false); setLoadG(false); setLoadT(false); setLoadBPT(false); setLoadSD(false); setLoadPK(false); setLoadCK(false);
        // Écriture cache phase 1 immédiatement (sans attendre phase 2)
        try {
          const existing = JSON.parse(localStorage.getItem('decisionnel_cache') || '{}');
          localStorage.setItem('decisionnel_cache', JSON.stringify({
            ...existing,
            _version: CACHE_VERSION, _paramsKey: buildCacheKey(params),
            kpi: kpiR.data, graphique: graphR.data, tableau: tabR.data,
            budgetParType: bptR.data.rows || [], budgetParTypeCA: bptR.data.budgets || {},
            stockDestockage: sdR.data || [], projetKpi: pkR.data, commercialKpi: ckR.data,
            savedAt: new Date().toISOString(),
          }));
        } catch {}

        /* ── Phase 2 : données de détail (sur clic) — chargées après le rendu ── */
        Promise.all([
          api.get('/decisionnel/detail-ventes',       { params }),
          api.get('/decisionnel/detail-recouvrement', { params }),
          api.get('/decisionnel/detail-pn',           { params }),
          api.get('/decisionnel/detail-budget',       { params }),
          api.get('/decisionnel/detail-stock-ca',     { params }),
        ])
          .then(([dvR, drR, dpR, dbR, dscR]) => {
            setDetailVentes(dvR.data);
            setDetailRec(drR.data?.rows || []); setPtfAvant(drR.data?.ptfAvant || 0); setPtfApres(drR.data?.ptfApres || 0); setPtfAvantRows(drR.data?.ptfAvantRows || []); setPtfApresRows(drR.data?.ptfApresRows || []);
            setDetailPn(dpR.data); setDetailBudget(dbR.data); setDetailStockCa(dscR.data || []);
            const savedAt = new Date().toISOString();
            setLastFetch(savedAt);
            try { localStorage.setItem('decisionnel_cache', JSON.stringify({ _version: CACHE_VERSION, _paramsKey: buildCacheKey(params), kpi: kpiR.data, graphique: graphR.data, tableau: tabR.data, detailVentes: dvR.data, detailRec: drR.data?.rows || [], ptfAvant: drR.data?.ptfAvant || 0, ptfApres: drR.data?.ptfApres || 0, ptfAvantRows: drR.data?.ptfAvantRows || [], ptfApresRows: drR.data?.ptfApresRows || [], detailPn: dpR.data, budgetParType: bptR.data.rows || [], budgetParTypeCA: bptR.data.budgets || {}, stockDestockage: sdR.data || [], projetKpi: pkR.data, commercialKpi: ckR.data, detailBudget: dbR.data, detailStockCa: dscR.data || [], savedAt })); } catch {}
          })
          .catch(e => { if (e?.response?.status !== 401) console.error('[decisionnel] détails:', e?.message); })
          .finally(() => { setLoadDV(false); setLoadDR(false); setLoadDP(false); setLoadDB(false); setLoadDSC(false); });
      })
      .catch(e => { if (e?.response?.status !== 401) setError(e?.response?.data?.message || 'Erreur chargement données'); setLoadK(false); setLoadG(false); setLoadT(false); setLoadBPT(false); setLoadSD(false); setLoadPK(false); setLoadCK(false); setLoadDV(false); setLoadDR(false); setLoadDP(false); setLoadDB(false); setLoadDSC(false); });

    api.get('/decisionnel/duree-traitement', { params })
      .then(r => setDureeData(r.data))
      .catch(e => { if (e?.response?.status !== 401) { console.error('[duree-traitement]', e?.response?.data?.message || e.message); setDureeData(null); } })
      .finally(() => setLoadDuree(false));

    api.get('/decisionnel/notaire-soon', { params })
      .then(r => setNsRows(r.data))
      .catch(() => setNsRows([]))
      .finally(() => setLoadNS(false));

    setLoadLots(true);
    api.get('/decisionnel/statut-lots', { params: { projetIds: params.projetIds } })
      .then(r => setLotsRows(r.data))
      .catch(() => setLotsRows([]))
      .finally(() => setLoadLots(false));

    setLoadEncours(true);
    api.get('/decisionnel/situation-encours', { params })
      .then(r => setEncoursRows(r.data))
      .catch(() => setEncoursRows([]))
      .finally(() => setLoadEncours(false));

    setLoadSitComm(true);
    api.get('/decisionnel/situation-commerciale', { params: { projetIds: params.projetIds } })
      .then(r => setSituationCommerciale(r.data))
      .catch(() => setSituationCommerciale([]))
      .finally(() => setLoadSitComm(false));

  }, [buildParams, includeOldSigned, oldSignedBefore, stockTypeOpts]);

  /* ── détail Stock/Déstockage (sur clic) ───────────────────────────────────── */
  const fetchStockCumul = useCallback((projetId, typeBien, tranche) => {
    const params = { ...buildParams(), projetId, typeBien, tranche };
    if (includeOldSigned) { params.includeOldSigned = '1'; params.oldSignedBefore = oldSignedBefore; }
    setLoadDSCu(true);
    api.get('/decisionnel/detail-stock-cumul', { params })
      .then(r => setDetailStockCumul(r.data || []))
      .catch(() => setDetailStockCumul([]))
      .finally(() => setLoadDSCu(false));
  }, [buildParams, includeOldSigned, oldSignedBefore]);

  const fetchStockInitial = useCallback((projetId, typeBien, tranche) => {
    const params = { ...buildParams(), projetId, typeBien, tranche };
    setLoadDSI(true);
    api.get('/decisionnel/detail-stock-initial', { params })
      .then(r => setDetailStockInitial(r.data || []))
      .catch(() => setDetailStockInitial([]))
      .finally(() => setLoadDSI(false));
  }, [buildParams]);

  /* ── effects ──────────────────────────────────────────────────────────────── */
  // Réinitialiser projets si ville change
  useEffect(() => {
    if (villeOpt !== prevVille.current) { setProjetOpts([]); prevVille.current = villeOpt; }
  }, [villeOpt]);

  // Chargement filtres
  useEffect(() => {
    setLoadF(true);
    const params = { debut, fin };
    if (filterProjetIds.length) params.projetIds = filterProjetIds.join(',');
    api.get('/decisionnel/filters', { params })
      .then(r => {
        setFilters(prev => {
          const villes  = filterProjetIds.length && prev ? prev.villes  : r.data.villes;
          const projets = filterProjetIds.length && prev ? prev.projets : r.data.projets;
          return { villes, projets, tranches: r.data.tranches, types: r.data.types, commerciaux: r.data.commerciaux };
        });
        if (trancheOpt && !r.data.tranches.includes(trancheOpt.value))          setTrancheOpt(null);
        if (comOpt && comOpt.value !== 'all' && !r.data.commerciaux.find(c => c.id === comOpt.value)) setComOpt(null);
      })
      .catch(e => { if (e?.response?.status !== 401) setError(e?.response?.data?.message || 'Erreur chargement filtres'); })
      .finally(() => setLoadF(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProjetIds.join(','), debut, fin]);

  // Cache localStorage au montage
  const CACHE_VERSION = 'v4';
  const CACHE_TTL_MS  = 5 * 60 * 1000; // 5 minutes : skip le rechargement si données fraîches
  const buildCacheKey = (p) => `${p.debut}|${p.fin}|${p.projetIds || ''}`;

  useEffect(() => {
    try {
      const raw = localStorage.getItem('decisionnel_cache');
      if (!raw) return;
      const c = JSON.parse(raw);
      if (c._version !== CACHE_VERSION) { localStorage.removeItem('decisionnel_cache'); return; }
      if (c.kpi)           setKpi(c.kpi);
      if (c.graphique)     setGraphique(c.graphique);
      if (c.tableau)       setTableau(c.tableau);
      if (c.detailVentes)  setDetailVentes(c.detailVentes);
      if (Array.isArray(c.detailRec))     setDetailRec(c.detailRec);
      if (c.ptfAvant != null)             setPtfAvant(c.ptfAvant);
      if (c.ptfApres != null)             setPtfApres(c.ptfApres);
      if (Array.isArray(c.ptfAvantRows))  setPtfAvantRows(c.ptfAvantRows);
      if (Array.isArray(c.ptfApresRows))  setPtfApresRows(c.ptfApresRows);
      if (c.detailPn)      setDetailPn(c.detailPn);
      if (c.budgetParType) setBudgetParType(c.budgetParType);
      if (c.budgetParTypeCA) setBudgetParTypeCA(c.budgetParTypeCA);
      if (c.stockDestockage) setStockDestockage(c.stockDestockage);
      if (c.projetKpi)     setProjetKpi(c.projetKpi);
      if (c.commercialKpi) setCommercialKpi(c.commercialKpi);
      if (c.detailBudget)  setDetailBudget(c.detailBudget);
      if (c.detailStockCa) setDetailStockCa(c.detailStockCa);
      if (c.savedAt)       setLastFetch(c.savedAt);
      // Skip le loadData initial si le cache est frais (< 5 min) et correspond aux mêmes params
      const ageMs = c.savedAt ? Date.now() - new Date(c.savedAt).getTime() : Infinity;
      const currentKey = buildCacheKey(buildParams());
      if (ageMs < CACHE_TTL_MS && c._paramsKey === currentKey) {
        cacheSkipLoadRef.current = true;
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chargement notaires
  useEffect(() => {
    const params = {};
    if (filterProjetIds.length) params.projetIds = filterProjetIds.join(',');
    api.get('/decisionnel/notaires', { params })
      .then(r => setNotairesList(r.data))
      .catch(() => setNotairesList([]));
    setNotaireOpts([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProjetIds.join(',')]);

  // Notaire-soon (lazy, déclenché par activeDetail)
  const activeNSTab = activeDetail?.type === 'notaire-soon' ? (activeDetail.tab || '') : null;
  useEffect(() => {
    if (activeNSTab === null) return;
    const params = { ...buildParams() };
    if (activeNSTab) params.tab = activeNSTab;
    setLoadDNS(true); setDetailNS([]);
    api.get('/decisionnel/notaire-soon', { params })
      .then(r => setDetailNS(r.data))
      .catch(() => setDetailNS([]))
      .finally(() => setLoadDNS(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNSTab]);

  // Rechargement stock-destockage seul (option "anciens contrats signés notaire" / filtre type de bien)
  const skipFirstSD = useRef(true);
  useEffect(() => {
    if (skipFirstSD.current) { skipFirstSD.current = false; return; }
    const params = { ...buildParams() };
    if (includeOldSigned) { params.includeOldSigned = '1'; params.oldSignedBefore = oldSignedBefore; }
    if (stockTypeOpts.length) params.stockTypes = stockTypeOpts.join(',');
    setLoadSD(true);
    api.get('/decisionnel/stock-destockage', { params })
      .then(r => setStockDestockage(r.data || []))
      .catch(() => {})
      .finally(() => setLoadSD(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeOldSigned, oldSignedBefore, stockTypeOpts]);

  // Debounce filtres non-date
  useEffect(() => {
    clearTimeout(debounceRef.current);
    // Skip le premier déclenchement (montage initial) si le cache est frais
    if (cacheSkipLoadRef.current) { cacheSkipLoadRef.current = false; return; }
    debounceRef.current = setTimeout(() => loadData(), 600);
    return () => clearTimeout(debounceRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [villeOpt, projetOpts, trancheOpt, typeOpt, comOpt, titreOpt, notaireOpts]);

  /* ── section helpers ──────────────────────────────────────────────────────── */
  const toggleSec = useCallback((id) => {
    setSectionCollapsed(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem('dec_sec_collapsed', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const moveSec = useCallback((fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    setSectionOrder(prev => {
      const arr = [...prev], fi = arr.indexOf(fromId), ti = arr.indexOf(toId);
      if (fi === -1 || ti === -1) return prev;
      arr.splice(fi, 1); arr.splice(ti, 0, fromId);
      try { localStorage.setItem('dec_sec_order', JSON.stringify(arr)); } catch {}
      return arr;
    });
  }, []);

  const scrollToFilters = useCallback(() => {
    filtersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const resetFilters = () => {
    setDebut(yearStart()); setFin(today());
    setInputDebut(yearStart()); setInputFin(today());
    setVilleOpt(null); setProjetOpts([]); setTrancheOpt(null);
    setTypeOpt(null);  setComOpt(null);   setTitreOpt([]);
    setStatutNotaireOpts(['CHEZ', 'EN_COURS']); setNotaireOpts([]);
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => -d);
    else { setSortKey(key); setSortDir(-1); }
  };

  const toggleComHidden = (comId) => {
    setComHidden(prev => {
      const next = new Set(prev);
      if (next.has(comId)) next.delete(comId); else next.add(comId);
      try { localStorage.setItem('decisionnel_com_hidden', JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  return {
    /* filter state */
    debut, fin, inputDebut, inputFin, setInputDebut, setInputFin,
    villeOpt, setVilleOpt, projetOpts, setProjetOpts,
    trancheOpt, setTrancheOpt, typeOpt, setTypeOpt,
    comOpt, setComOpt, titreOpt, setTitreOpt,
    statutNotaireOpts, setStatutNotaireOpts,
    notaireOpts, setNotaireOpts, notairesList,
    /* data */
    filters, kpi, graphique, tableau, sorted,
    detailVentes, detailRec, ptfAvant, ptfApres, ptfAvantRows, ptfApresRows, detailPn,
    projetKpi, commercialKpi, budgetParType, budgetParTypeCA, stockDestockage, detailBudget,
    detailStockCa, detailStockCumul, detailStockInitial, fetchStockCumul, fetchStockInitial,
    includeOldSigned, setIncludeOldSigned, oldSignedBefore, setOldSignedBefore,
    stockTypeOpts, setStockTypeOpts,
    showStockFilters, setShowStockFilters,
    detailNS, nsRows, lotsRows, encoursRows, situationCommerciale, loadSitComm, dureeData, lastFetch,
    /* loading */
    loadF, loadK, loadG, loadT, loadDV, loadDR, loadDP,
    loadPK, loadCK, loadBPT, loadSD, loadDB, loadDSC, loadDSCu, loadDSI, loadDNS, loadNS, loadLots, loadEncours, loadDuree,
    error, setError,
    /* ui state */
    activeDetail, setActiveDetail,
    showAllProjets, setShowAllProjets,
    showAllCom, setShowAllCom,
    comConfigOpen, setComConfigOpen,
    comHidden, setComHidden,
    sortKey, toggleSort,
    sectionOrder, sectionCollapsed, sectionUnits, setUnit,
    dragSecId, setDragSecId, dragOverSecId, setDragOverSecId,
    /* computed */
    projetsDisponibles, filterProjetIds, activeFilterPills, datesDirty, hasFilters,
    /* refs */
    filtersRef,
    /* actions */
    loadData, buildParams, resetFilters, toggleSec, moveSec, scrollToFilters, toggleComHidden,
    setDebut, setFin,
  };
};
